import { APP_ROUTES_PATIENT, APP_ROUTES_RECP_ADD_INTAKE, genderOptions } from '@data'
import { usePatient, useUserRole } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { ADMIN_MODAL_TYPE, useModalStore } from '@modal'
import { addPatientSchema } from '@schema'
import { useAppSelector } from '@store/hooks'
import { AddPatientFormData } from '@type/patient.types'
import { Button, CheckBox, DateInputField, FormButton, FormInputField, Modal, SelectInputField } from '@ui'
import { getError, replaceRouteParams, showToast } from '@util'
import { Box, Text } from 'grommet'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useProductModules } from '../../../../modules/useProductModules'

type Props = {
  onClose: () => void
}
export const optionRelationship = ['Mother', 'Father', 'Spouse', 'Brother', 'Sister', 'Son', 'Daughter', 'Grandfather']

export const AddPatient = ({ onClose }: Props) => {
  const { isAdmin, isSuperAdmin } = useUserRole()
  const { isModuleEnabled } = useProductModules()
  const portalEnabled = isModuleEnabled('patient_portal')
  const { user } = useAppSelector(state => state.user)
  const permissions = user?.permissions
  const canStartIntake =
    isSuperAdmin || (!!permissions?.intake?.includes('create') && !!permissions?.intake?.includes('manage'))
  const { openModal, closeModal } = useModalStore()
  const { createPatient } = usePatient()
  const navigate = useNavigate()
  const methods = useForm<AddPatientFormData>({
    defaultValues: {
      charge_registration_fee: false,
      date_of_birth: new Date('01/01/2000').toISOString()
    },
    resolver: yupResolver(addPatientSchema)
  })

  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = methods

  const onSubmit = async (formRecObj: AddPatientFormData) => {
    const { charge_registration_fee, email, ...patientAttrs } = formRecObj
    const formInputVal = {
      data: {
        attributes: {
          ...patientAttrs,
          charge_registration_fee: !!charge_registration_fee
        },
        relationships: {
          role: {
            data: {
              id: 'patient',
              type: 'role'
            }
          },
          ...(portalEnabled && email
            ? {
                user: {
                  data: {
                    id: email,
                    type: 'user'
                  }
                }
              }
            : {})
        },
        type: 'patient'
      }
    }
    try {
      const response = await createPatient(formInputVal)
      showToast('Patient created successfully')
      onClose()
      openModal(ADMIN_MODAL_TYPE.SUCCESS_MODAL, {
        children: (
          <div className="flex flex-col gap-4">
            {canStartIntake && (
              <Button
                label="Start Intake"
                onClick={() => {
                  navigate(replaceRouteParams(APP_ROUTES_RECP_ADD_INTAKE, { patientId: response.data?.id }))
                  closeModal(ADMIN_MODAL_TYPE.SUCCESS_MODAL)
                }}
                size={'wide'}
              />
            )}
            <Button
              kinds="secondary"
              label={isAdmin || isSuperAdmin ? 'Back' : 'Return to patient directory'}
              onClick={() => {
                navigate(isAdmin || isSuperAdmin ? APP_ROUTES_PATIENT : '/')
                closeModal(ADMIN_MODAL_TYPE.SUCCESS_MODAL)
              }}
              size={'wide'}
              style={{ backgroundColor: '#F5F5F5' }}
            />
          </div>
        ),
        description: 'Patient details have been saved. View patients chart by clicking the button below',
        title: 'Patient Added Successfully'
      })
    } catch (error: any) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Add Patient">
        <form className="flex flex-col md:w-[750px] w-full overflow-auto flex-1" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col overflow-auto gap-6 p-6 flex-1">
            <div className="grid md:grid-cols-3 grid-cols-1 gap-6">
              <Box pad="0">
                <FormInputField label="First Name" name="first_name" placeholder="" />
              </Box>
              <Box pad="0">
                <FormInputField label="Middle Name" name="middle_name" placeholder="" />
              </Box>
              <Box pad="0">
                <FormInputField label="Last Name" name="last_name" placeholder="" />
              </Box>
            </div>
            <div>
              <SelectInputField
                label="Gender"
                labelKey={'name'}
                name="gender"
                options={genderOptions}
                placeholder="Select"
                valueKey={{ key: 'value', reduce: true }}
              />
            </div>
            {portalEnabled ? (
              <div>
                <FormInputField label="Portal email (invite)" name="email" placeholder="" />
                <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                  Sends a patient portal invitation when provided.
                </Text>
              </div>
            ) : null}
            <div>
              <DateInputField label="Date Of Birth" name="date_of_birth" />
            </div>
            <div>
              <FormInputField label="Phone Number" name="phone_number" placeholder="" />
            </div>
            <Controller
              control={control}
              name="charge_registration_fee"
              render={({ field: { onChange, value } }) => (
                <Box align="center" direction="row" gap="small" justify="between">
                  <Text size="small">Charge registration fee</Text>
                  <CheckBox checked={!!value} handleChange={checked => onChange(checked)} />
                </Box>
              )}
            />
            <Text color="dark-4" size="xsmall">
              Creates an empty invoice so staff can add the registration charge manually in Billing.
            </Text>
          </div>
          <FormButton disabled={isSubmitting} isLoading={isSubmitting} onClose={onClose} submit="Add Patient" />
        </form>
      </Modal>
    </FormProvider>
  )
}
